import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { PromoHolderDto } from '@lunary/api-client'
import { ApiError } from '../../shared/api/client'
import { formatNumber } from '../../shared/ui/charts/scale'
import { PROMO_KIND_LABELS, formatDateTime } from './labels'
import { PromoDialog } from './promo-dialog'
import { PROMO_PAGE, useAdjustPromo, usePromo } from './queries'
import styles from './lunars.module.css'

const ROLE_LABELS: Readonly<Record<string, string>> = { expert: 'Таролог', admin: 'Админ' }
const FORMAT_LABELS: Readonly<Record<string, string>> = { question: 'один вопрос', consultation: 'консультация' }
const GIFT_STATUS_LABELS: Readonly<Record<string, string>> = {
  pending: 'ждёт получателя',
  claimed: 'принят',
  used: 'разбор заказан',
  cancelled: 'отменён',
  expired: 'истёк',
}

export function PromoTab() {
  const [offset, setOffset] = useState(0)
  const [editing, setEditing] = useState<PromoHolderDto | null>(null)
  const promo = usePromo(offset)
  const adjust = useAdjustPromo()
  const data = promo.data
  const error = adjust.error ? (adjust.error instanceof ApiError ? adjust.error.message : 'Не получилось, попробуйте ещё раз') : null
  const close = () => {
    adjust.reset()
    setEditing(null)
  }

  return (
    <div className={styles.stack}>
      <p className={styles.caption}>
        Промо-лунары таролог тратит в приложении на «Подарить разбор»: цена подарка списывается с промо-баланса, а не с её лунаров. Это
        реклама, а не продажа: платежа нет, чек в «Мой налог» не нужен. Неиспользованный или отменённый подарок возвращается на промо-баланс.
      </p>
      <div className="card-panel">
        <div className={styles.scroll}>
          <table className="users-table">
            <thead>
              <tr>
                <th>Таролог</th>
                <th>Промо-баланс</th>
                <th>Начислено</th>
                <th>На подарки</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data?.holders.map((h) => (
                <tr key={h.user_id}>
                  <td>
                    <Link to="/users/$userId" params={{ userId: h.user_id }}>
                      {h.display_name ?? h.email ?? 'Без имени'}
                    </Link>
                    <div className="muted">{ROLE_LABELS[h.role] ?? h.role}</div>
                  </td>
                  <td className={styles.big}>{formatNumber(h.balance)}</td>
                  <td>{formatNumber(h.granted)}</td>
                  <td>
                    {formatNumber(h.gifted)}
                    <div className="muted">подарков: {formatNumber(h.gifts_count)}</div>
                  </td>
                  <td>
                    <button className="secondary" onClick={() => setEditing(h)}>
                      Начислить
                    </button>
                  </td>
                </tr>
              ))}
              {data?.holders.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Тарологов пока нет. Роль выдаётся командой set-user-role.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card-panel">
        <h2>История</h2>
        <div className={styles.scroll}>
          <table className="users-table">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Таролог</th>
                <th>Операция</th>
                <th>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((r) => (
                <tr key={r.id}>
                  <td>{formatDateTime(r.created_at)}</td>
                  <td>{r.user_name ?? '—'}</td>
                  <td>
                    {PROMO_KIND_LABELS[r.kind] ?? r.kind}
                    <div className="muted">
                      {r.gift_format
                        ? `${FORMAT_LABELS[r.gift_format] ?? r.gift_format} · ${GIFT_STATUS_LABELS[r.gift_status ?? ''] ?? r.gift_status ?? ''}`
                        : [r.comment, r.actor_name && `кто: ${r.actor_name}`].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td className={styles.mono}>{r.amount > 0 ? `+${formatNumber(r.amount)}` : `−${formatNumber(-r.amount)}`}</td>
                </tr>
              ))}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    Начислений и подарков пока не было.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {promo.isPending && <div className="spinner" aria-label="Загрузка" />}
        {promo.isError && <p className="error-box">Не удалось загрузить промо-балансы.</p>}
        {data && data.total > PROMO_PAGE && (
          <div className={styles.pager}>
            <button className="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PROMO_PAGE))}>
              Назад
            </button>
            <button className="secondary" disabled={offset + PROMO_PAGE >= data.total} onClick={() => setOffset(offset + PROMO_PAGE)}>
              Дальше
            </button>
          </div>
        )}
      </div>
      <PromoDialog
        holder={editing}
        pending={adjust.isPending}
        error={error}
        onSubmit={(amount, comment) => {
          if (editing) adjust.mutate({ userId: editing.user_id, amount, comment }, { onSuccess: close })
        }}
        onClose={close}
      />
    </div>
  )
}
